import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import { Container, Form } from "react-bootstrap";
import "./index.css";
import {
  ArrowLeftIcon,
  Cancel01Icon,
  ClockIcon,
  PlusIcon,
} from "../../../assets/icons";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { goBack } from "../../../utils/otherHelopers";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchPortfolioDetail,
  updatePortfolio,
  createPortfolio,
} from "../../../redux/slice/companySlice";
import InputField from "../../../components/form/InputField";
import { formatMomentDate } from "../../../utils/dateHelper";
import StatusChip from "../../../components/statusChip/StatusChip";
import CommonButton from "../../../components/commonButton";
import { updatePortfolioSchema } from "../../../schema/validationSchemas";
import { useToast } from "../../../context/TostContext";
import { useLoader } from "../../../context/LoaderContext";
import { FormLabel } from "../../../components/form/utils";
import CustomSelect from "../../../components/form/CustomSelect";



const PortfolioForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const lastTagRef = useRef(null);
  const {
    portfolioDetail,
    portfolioLoading,
    portfolioUpdateLoading,
  } = useSelector((state) => state.company);
  const toast = useToast();
  const { setLoading } = useLoader();

  const { companyId, id: portfolioId } = useParams();
  const isCreateMode = portfolioId === "create" || !portfolioId;
  const dispatch = useDispatch();

  const headerPortalTarget = document.getElementById("header-portal-target");

  const renderHeaderContent = () => (
    <div className="d-flex align-items-center justify-content-between w-100">
      <div className="d-flex align-items-center gap-2">
        <button
          onClick={handleBack}
          type="button"
          className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center primary"
        >
          <ArrowLeftIcon
            size={22} />
        </button>
        <h4 className="fw-semibold mb-0 text-primary fs-lg">
          {isCreateMode ? "Add Portfolio" : "Portfolio Details"}
        </h4>
      </div>
      {(!portfolioDetail && !portfolioLoading && !isCreateMode) ? null : (
        <div className="d-flex gap-2 pe-3">
          <CommonButton
            title="Cancel"
            variant="outline"
            onClick={handleBack}
          />
          <CommonButton
            title={
              portfolioUpdateLoading
                ? "Saving..."
                : isCreateMode
                  ? "Save"
                  : "Edit"
            }
            variant="primary"
            onClick={handleSave}
            disabled={portfolioUpdateLoading}
          />
        </div>
      )}
    </div>
  );

  const loadPortfolio = useCallback(async () => {
    if (!portfolioId || isCreateMode) return;

    setLoading(true);
    try {
      await dispatch(fetchPortfolioDetail({ companyId, id: portfolioId })).unwrap();
    } catch (error) {
      console.error("Failed to fetch portfolio detail:", error);
    } finally {
      setLoading(false);
    }
  }, [portfolioId, isCreateMode, dispatch, setLoading, companyId]);

  useEffect(() => {
    loadPortfolio();
  }, [loadPortfolio]);

  const [formData, setFormData] = useState({
    category: "",
    title: "",
    description: "",
    status: "published",
    startDate: "",
    endDate: "",
    project_url: "",
    tags: [],
    media: [],
  });

  const [tagInput, setTagInput] = useState("");
  const [removedMediaIds, setRemovedMediaIds] = useState([]);

  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (portfolioDetail) {
      setFormData({
        category: portfolioDetail.category || "",
        title: portfolioDetail?.title || "",
        description: portfolioDetail.description || "",
        status: portfolioDetail.status || "published",
        startDate: portfolioDetail.start_date
          ? formatMomentDate(portfolioDetail.start_date, "YYYY-MM-DD")
          : "",
        endDate: portfolioDetail.end_date
          ? formatMomentDate(portfolioDetail.end_date, "YYYY-MM-DD")
          : "",
        project_url: portfolioDetail.project_url || "",
        tags: portfolioDetail.tags ? [...portfolioDetail.tags] : [],
        media: portfolioDetail.media ? [...portfolioDetail.media] : [],
      });
      setRemovedMediaIds([]);
    }
  }, [portfolioDetail]);

  const handleBack = () => goBack(navigate, location, `/company/${companyId}`);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTagInputChange = (e) => {
    setTagInput(e.target.value);
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const newTag = tagInput.trim();
      if (newTag && !formData.tags.includes(newTag)) {
        setFormData((prev) => ({
          ...prev,
          tags: [...prev.tags, newTag],
        }));
        setTagInput("");
      }
    } else if (e.key === "Backspace" && !tagInput && formData.tags.length > 0) {
      handleRemoveTag(formData.tags.length - 1);
    }
  };

  const handleRemoveTag = (index) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index),
    }));
  };


  useEffect(() => {
    if (lastTagRef.current) {
      lastTagRef.current.focus();
    }
  }, [formData.tags.length]);

  const handleImageChange = (file) => {
    if (!file) return;

    setFormData((prev) => ({
      ...prev,
      media: [
        ...prev.media,
        {
          file, // REAL File
          preview: URL.createObjectURL(file),
          isNew: true,
        },
      ],
    }));
  };

  const handleRemoveImage = (index) => {
    const itemToRemove = formData.media[index];
    const mediaId = itemToRemove.id;
    if (!itemToRemove.isNew && mediaId) {
      setRemovedMediaIds((prev) => [...prev, mediaId]);
    }
    setFormData((prev) => ({
      ...prev,
      media: prev.media.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    try {
      await updatePortfolioSchema.validate(
        {
          ...formData,
          start_date: formData.startDate,
          end_date: formData.endDate,
        },
        { abortEarly: false },
      );
      setValidationErrors({});

      const fd = new FormData();

      // Basic fields
      fd.append("title", formData?.title);
      if (formData.description) fd.append("description", formData.description);
      fd.append("category", formData.category);
      fd.append("status", formData.status);
      fd.append("project_url", formData.project_url || "");

      if (formData.startDate) fd.append("start_date", formData.startDate);
      if (formData.endDate) fd.append("end_date", formData.endDate);
      if (formData.client_name) fd.append("client_name", formData.client_name);

      // Tags (backend accepts JSON string)
      fd.append(
        "tags",
        JSON.stringify(formData.tags.filter((t) => t && t.trim())),
      );

      // Removed media IDs → goes to req.body.removed_image
      if (removedMediaIds.length > 0) {
        fd.append("removed_images", JSON.stringify(removedMediaIds));
      }

      // New files → goes to req.files
      formData.media
        .filter((m) => m.isNew)
        .forEach((m) => {
          fd.append("media", m.file); // IMPORTANT: same key
        });

      const action = isCreateMode ? createPortfolio : updatePortfolio;
      const payload = isCreateMode
        ? { companyId, data: fd }
        : { companyId, id: portfolioId, data: fd };

      const result = await dispatch(action(payload)).unwrap();

      if (result?.success) {
        toast.success(
          result?.message || `Portfolio ${isCreateMode ? "created" : "updated"} successfully!`,
        );
        if (isCreateMode) {
          navigate(-1);
        } else {
          await dispatch(fetchPortfolioDetail({ companyId, id: portfolioId })).unwrap();
        }
      }
    } catch (error) {
      if (error.name === "ValidationError") {
        const errors = {};
        error.inner.forEach((err) => {
          errors[err.path] = err.message;
        });
        setValidationErrors(errors);
        toast.error("Please fix the validation errors");
      } else {
        console.error("Save failed:", error);
        toast.error(error?.message || "Failed to update portfolio");
      }
    }
  };



  if (!portfolioDetail && !portfolioLoading && !isCreateMode) {
    return (
      <>
        {headerPortalTarget ? (
          createPortal(renderHeaderContent(), headerPortalTarget)
        ) : (
          <div className="d-flex align-items-center justify-content-between mb-3 w-100">
            <div className="d-flex align-items-center gap-2">
              <button
                onClick={handleBack}
                type="button"
                className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center primary"
              >
                <ArrowLeftIcon size={22} />
              </button>
              <h4 className="fw-semibold mb-0 text-primary fs-lg">
                {isCreateMode ? "Add Portfolio" : "Portfolio Details"}
              </h4>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {headerPortalTarget && createPortal(renderHeaderContent(), headerPortalTarget)}
      <Container fluid className="px-0">
        {!headerPortalTarget && (
          <div className="d-flex align-items-center justify-content-between mb-3 w-100">
            <div className="d-flex align-items-center gap-2">
              <button
                onClick={handleBack}
                type="button"
                className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center primary"
              >
                <ArrowLeftIcon size={22} />
              </button>
              <h4 className="fw-semibold mb-0 text-primary fs-lg">
                {isCreateMode ? "Add Portfolio" : "Portfolio Details"}
              </h4>
            </div>
            <div className="d-flex gap-2 pe-3">
              <CommonButton
                title="Cancel"
                variant="outline"
                onClick={handleBack}
              />
              <CommonButton
                title={
                  portfolioUpdateLoading
                    ? "Saving..."
                    : isCreateMode
                      ? "Save"
                      : "Edit"
                }
                variant="primary"
                onClick={handleSave}
                disabled={portfolioUpdateLoading}
              />
            </div>
          </div>
        )}

        <div className={`portfolio-details-grid overflow-hidden`}>
          {/* Edit Form */}
          <div className="grid-6 overflow-auto scroll-0">
            <div className="white-box2 d-flex flex-column">
              {[
                {
                  id: "title",
                  label: "Portfolio Name",
                  placeholder: "Enter Portfolio Name",
                  className: "w-100 fw-bold",
                  required: true,
                },
                {
                  id: "category",
                  label: "Portfolio Category",
                  placeholder: "e.g. SaaS / Engineering Software",
                  required: true,
                },
                {
                  id: "description",
                  label: "Description",
                  placeholder: "Enter Portfolio Description",
                  type: "textarea",
                  rows: 6,
                },
                {
                  id: "status",
                  label: "Status",
                  type: "select",
                  options: [
                    { value: "published", label: "Published" },
                    { value: "draft", label: "Draft" },
                    // { value: "completed", label: "Completed" },
                  ],
                },
                {
                  row: [
                    { id: "startDate", label: "Start Date", type: "date" },
                    { id: "endDate", label: "End Date", type: "date" },
                  ],
                },
                {
                  id: "project_url",
                  label: "Relevant Links",
                  placeholder: "https://example.com",
                  icon: formData.project_url ? (
                    <span
                      onClick={() => setFormData((prev) => ({ ...prev, project_url: "" }))}
                      className="cursor-pointer d-flex align-items-center justify-content-center"
                    >
                      <Cancel01Icon size={18} />
                    </span>
                  ) : null,
                },
              ].map((field, idx, arr) => (
                <React.Fragment key={field.id || idx}>
                  {field.type === "textarea" ? (
                    <InputField
                      className="mb-3"
                      label={{ name: field.label }}
                      required={field.required}
                      input={{
                        id: field.id,
                        name: field.id,
                        as: "textarea",
                        rows: field.rows || 4,
                        placeholder: field.placeholder,
                        value: formData[field.id],
                        onChange: handleInputChange,
                      }}
                      validation={{
                        isInvalid: !!validationErrors[field.id],
                        message: validationErrors[field.id],
                      }}
                    />
                  ) : field.type === "select" ? (
                    <CustomSelect
                      className="mb-3"
                      label={{ name: field.label }}
                      required={field.required}
                      name={field.id}
                      value={formData[field.id]}
                      onChange={handleSelectChange}
                      options={field.options || []}
                      validation={{
                        isInvalid: !!validationErrors[field.id],
                        message: validationErrors[field.id],
                      }}
                      isSearchable={false}
                    />
                  ) : field.row ? (
                    <div className="row mb-3">
                      {field.row.map((s) => {
                        const errKey = s.id === "startDate" ? "start_date" : s.id === "endDate" ? "end_date" : s.id;
                        return (
                          <div className="col-6" key={s.id}>
                            <InputField
                              label={{ name: s.label }}
                              required={s.required}
                              input={{
                                id: s.id,
                                name: s.id,
                                type: s.type || "text",
                                placeholder: s.placeholder || `Enter ${s.label.toLowerCase()}`,
                                value: formData[s.id],
                                onChange: handleInputChange,
                              }}
                              validation={{
                                isInvalid: !!validationErrors[errKey],
                                message: validationErrors[errKey],
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <InputField
                      className="mb-3"
                      label={{ name: field.label }}
                      required={field.required}
                      input={{
                        id: field.id,
                        name: field.id,
                        type: field.type || "text",
                        placeholder: field.placeholder || `Enter ${field.label.toLowerCase()}`,
                        value: formData[field.id],
                        onChange: handleInputChange,
                        icon: field.icon,
                      }}
                      validation={{
                        isInvalid: !!validationErrors[field.id === "project_url" ? "project_url" : field.id],
                        message: validationErrors[field.id === "project_url" ? "project_url" : field.id],
                      }}
                    />
                  )}
                  {idx < arr.length - 1 && <hr className="m-0" />}
                </React.Fragment>
              ))}
              <hr className="m-0" />
              <Form.Group className="mb-3" controlId="formBasicTags">
                <FormLabel className="fw-bold text-secondary" labelName="Relevant Tags" required />
                <div className="d-flex flex-wrap gap-2 p-3 border rounded">
                  {formData.tags.map((tag, index) => (
                    <div
                      key={index}
                      className="d-flex align-items-center border rounded-pill px-3 py-1 gap-2 bg-light"
                      style={{ height: "32px" }}
                    >
                      <span className="text-muted small">{tag}</span>
                      <button
                        className="btn btn-link text-muted p-0 d-flex"
                        onClick={() => handleRemoveTag(index)}
                      >
                        <Cancel01Icon size={14} />
                      </button>
                    </div>
                  ))}
                  <input
                    type="text"
                    value={tagInput}
                    onChange={handleTagInputChange}
                    onKeyDown={handleTagInputKeyDown}
                    placeholder={formData.tags.length === 0 ? "Type and press Enter to add tags" : "Add more..."}
                    className="border-0 shadow-none bg-transparent text-muted relavent-tags flex-grow-1"
                    style={{ minWidth: "150px", outline: "none" }}
                    ref={lastTagRef}
                  />
                </div>
              </Form.Group>

              <hr className="m-0" />
              <div className="p-3">
                <label className="form-label text-muted small">Photos</label>
                <div className="d-flex flex-wrap gap-3">
                  {formData.media.map((item, index) => (
                    <div
                      key={index}
                      className="position-relative border rounded"
                      style={{ width: "80px", height: "80px" }}
                    >
                      <img
                        src={item.isNew ? item.preview : item.file?.url}
                        alt={`img-${index}`}
                        className="w-100 h-100 rounded object-fit-cover"
                      />
                      <button
                        className="position-absolute text-white top-0 end-0 badge rounded-circle bg-secondary border-0 p-1 d-flex justify-content-center align-items-center"
                        style={{
                          width: "20px",
                          height: "20px",
                          cursor: "pointer",
                          transform: "translate(50%, -50%)",
                        }}
                        onClick={() => handleRemoveImage(index)}
                      >
                        <Cancel01Icon size={12} />
                      </button>
                    </div>
                  ))}
                  {formData.media.length < 5 && (
                    <div
                      style={{
                        width: "80px",
                        height: "80px",
                        borderStyle: "dashed",
                        borderSize: "1px",
                      }}
                      className="border-secondary rounded d-flex justify-content-center align-items-center position-relative"
                    >
                      <label
                        style={{ cursor: "pointer", width: "100%", height: "100%" }}
                        className="d-flex justify-content-center align-items-center m-0"
                      >
                        <PlusIcon size={24} />
                        <input
                          type="file"
                          className="position-absolute opacity-0 w-100 h-100"
                          accept="image/*"
                          style={{ cursor: "pointer", top: 0, left: 0 }}
                          onChange={(e) => handleImageChange(e.target.files[0])}
                        />
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="grid-6 overflow-auto scroll-0">
            <h4 className="text-muted fw-extrabold text-decoration-underline mb-4">
              Preview
            </h4>
            <div className="white-box2">
              <div className="d-flex flex-column gap-3">
                {formData.media.length > 0 ? (
                  <div className="post-swiper">
                    <Swiper
                      key={`swiper-${formData.media.length}-${formData.media.filter(m => m.isNew).length}`}
                      modules={[Pagination]}
                      spaceBetween={12}
                      loop
                      slidesPerView={1}
                      pagination={{
                        clickable: true,
                        // el: `.pagination-preview`,
                      }}
                    >
                      {formData.media.map((item, index) => (
                        <SwiperSlide key={index}>
                          <img
                            src={item.isNew ? item.preview : item.file?.url}
                            alt={formData?.title || "portfolio"}
                            className="post-image w-100"
                            style={{
                              borderRadius: "16px",
                              height: "350px",
                              objectFit: "cover",
                            }}
                          />
                        </SwiperSlide>
                      ))}
                      <div className="post-actions d-flex align-items-center justify-content-center p-0">
                        {formData?.media?.length > 0 && (
                          <div className="post-actions__pagination">
                            <div
                              className={`swiper-pagination pagination-${portfolioId}`}
                            />
                          </div>
                        )}
                      </div>
                    </Swiper>
                  </div>
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center bg-light rounded"
                    style={{ height: "350px" }}
                  >
                    <span className="text-muted">No photos</span>
                  </div>
                )}


                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <span className="sub-text mt-2">
                      {formData?.category || "Category"}
                    </span>
                    <h4 className="fw-semibold mb-0">
                      {formData?.title || "Portfolio Title"}
                    </h4>
                  </div>
                  <StatusChip
                    title={
                      formData?.status === "published"
                        ? "Published"
                        : formData?.status === "completed"
                          ? "Completed"
                          : "Draft"
                    }
                    bg={
                      formData?.status === "published"
                        ? "success"
                        : formData?.status === "completed"
                          ? "info"
                          : "secondary"
                    }
                    pill
                  />
                </div>

                <div>
                  <h6 className="fw-semibold mb-2">Description</h6>
                  {
                    formData?.description ? (
                      <p className="sub-text">
                        {formData?.description}
                      </p>
                    ) : (
                      <p className="sub-text">No description available</p>
                    )
                  }
                </div>

                <hr className="my-0" />

                <div className="white-box mb-0 bg-white d-flex flex-column">
                  <div className="white-box__grid basic-details">
                    <div className="d-flex gap-3 align-items-center">
                      <span className="white-box__icon primary">
                        <ClockIcon />
                      </span>
                      <div>
                        <small className="small">Portfolio Start Date</small>
                        <p style={{ paddingBottom: "2px" }} className="medium">
                          {formData?.startDate
                            ? formatMomentDate(formData.startDate, 
                              "DD MMMM, YYYY",
                            )
                            : "-"}
                        </p>
                      </div>
                    </div>
                    <div className="d-flex gap-3 align-items-center">
                      <span className="white-box__icon">
                        <ClockIcon />
                      </span>
                      <div>
                        <small className="small">Portfolio End Date</small>
                        <p style={{ paddingBottom: "2px" }} className="medium">
                          {formData?.endDate
                            ? formatMomentDate(formData.endDate, 
                              "DD MMMM, YYYY",
                            )
                            : "-"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <hr className="my-0" />

                <h6 className="fw-semibold mb-2">Relevant Links</h6>
                <div className="d-flex align-items-center">
                  {formData?.project_url ? (
                    <span className="sub-head">
                      {formData.project_url}
                    </span>
                  ) : (
                    <span className="text-muted small">No link added</span>
                  )}
                </div>

                <hr className="my-0" />

                <h6 className="fw-semibold mb-2">Tags</h6>
                <div className="d-flex flex-wrap gap-2">
                  {formData?.tags?.length > 0 ? (
                    formData.tags
                      .filter((t) => t)
                      .map((tag, index) => (
                        <span
                          key={index}
                          className="d-flex flex-wrap border px-3 py-1 rounded-pill text-danger"
                        >
                          {tag}
                        </span>
                      ))
                  ) : (
                    <span className="text-muted small">No tags added</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>


      </Container>
    </>
  );
};

export default PortfolioForm;
